import { collection, doc, getDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { TradeItem } from "./tradeTypes";

export type CommitSummary = {
  decrementedDocs: number;
  deletedDocs: number;
  addedDocs: number;
};

export async function commitTradeToMyCollection(params: {
  uid: string;
  sent: TradeItem[];      // items the active user is sending
  received: TradeItem[];  // items the active user is receiving
}): Promise<CommitSummary> {
  const { uid, sent, received } = params;
  const batch = writeBatch(db);
  const now = serverTimestamp();

  let decrementedDocs = 0;
  let deletedDocs = 0;
  let addedDocs = 0;

  // Decrement or delete from my collection (only origin: "my-collection", owned by me)
  for (const it of sent) {
    if (it.origin !== "my-collection" || it.ownerUid !== uid || !it.docId) continue;

    const ref = doc(db, "users", uid, "cards", it.docId);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;

    const current = snap.data() as any;
    const currentQty = Math.max(0, Number(current?.qty ?? 0));
    const newQty = Math.max(0, currentQty - Math.max(1, Math.floor(it.qty)));

    if (newQty > 0) {
      batch.update(ref, { qty: newQty, updatedAt: now });
      decrementedDocs += 1;
    } else {
      batch.delete(ref);
      deletedDocs += 1;
    }
  }

  // Add received items as new docs to my collection
  for (const it of received) {
    const ref = doc(collection(db, "users", uid, "cards"));
    batch.set(ref, {
      name: it.name,
      scryfall_id: it.scryfall_id ?? null,
      set: it.set ?? null,
      number: it.number ?? null,
      image: it.image ?? null,
      foil: Boolean(it.foil),
      qty: Math.max(1, Math.floor(it.qty)),
      price: typeof it.unitPrice === "number" ? it.unitPrice : null,
      for_sale: false,
      for_trade: false,
      status: "keep",
      notes: "SGtraded",
      custom_image_url: null,
      createdAt: now,
      updatedAt: now,
    });
    addedDocs += 1;
  }

  await batch.commit();
  return { decrementedDocs, deletedDocs, addedDocs };
}
